import React, { useMemo } from 'react'
import type { ProbeResult } from '../lib/api'
import { kindOf } from '../lib/api'

const GROUPS: { id: string; title: string }[] = [
  { id: 'command', title: 'COMMAND' },
  { id: 'public', title: 'PUBLIC SITES' },
  { id: 'media', title: 'MEDIA' },
  { id: 'ops', title: 'OPS / GATED' },
]

function chip(kind: string) {
  if (kind === 'up') return { label: 'UP', cls: 'text-[#2ee6c0] border-[#2ee6c0]/30 bg-[#2ee6c0]/10' }
  if (kind === 'gated') return { label: 'GATED', cls: 'text-amber-400 border-amber-400/30 bg-amber-400/10' }
  return { label: 'DOWN', cls: 'text-[#f43f5e] border-[#f43f5e]/30 bg-[#f43f5e]/10' }
}

export function ProbeGrid({ probes, loading }: { probes: ProbeResult[]; loading: boolean }) {
  const grouped = useMemo(() => {
    const map: Record<string, ProbeResult[]> = {}
    for (const p of probes) {
      const g = p.group || 'ops'
      if (!map[g]) map[g] = []
      map[g].push(p)
    }
    return map
  }, [probes])

  if (!probes.length && loading) {
    return (
      <div className="glass-panel rounded-md p-8 text-sm text-zinc-400">
        Sweeping public edge<span className="animate-blink">█</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {GROUPS.map((g) => {
        const items = grouped[g.id] || []
        if (!items.length) return null
        return (
          <section key={g.id}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[10px] tracking-[0.28em] text-zinc-500">{g.title}</h2>
              <span className="text-[10px] text-zinc-600 font-mono">{items.filter((i) => kindOf(i) === 'up').length}/{items.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map((p) => {
                const k = kindOf(p)
                const c = chip(k)
                const bar = Math.min(100, Math.max(8, 100 - p.ms / 8))
                return (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-panel rounded-md p-4 hover:border-amber-400/40 transition-colors block group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-100 truncate">{p.label || p.name}</div>
                        <div className="text-[10px] text-zinc-500 truncate mt-1">{p.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</div>
                      </div>
                      <span className={`shrink-0 text-[10px] tracking-widest px-2 py-1 rounded border ${c.cls}`}>{c.label}</span>
                    </div>
                    <div className="mt-4 h-1 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full ${k === 'up' ? 'bg-[#2ee6c0]' : k === 'gated' ? 'bg-amber-400' : 'bg-[#f43f5e]'}`}
                        style={{ width: `${bar}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500 font-mono">
                      <span>HTTP {p.http || '—'}</span>
                      <span>{p.ms}ms</span>
                    </div>
                  </a>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
